/**
 * Utilitários para telefones brasileiros.
 *
 * Internamente trabalhamos sempre com os dígitos "nacionais": DDD + número (10 ou 11 dígitos).
 * - Telefone é enviado para a API como dígitos nacionais  -> "53991234567"
 * - WhatsApp é enviado com o código do país (55)          -> "5553991234567"
 * A API remove a formatação e o WhatsApp é usado como "+" + número na Meta.
 */

export const COUNTRY_CODE = '55';
export const MAX_DIGITS = 11;

// DDDs válidos no Brasil (Anatel)
const VALID_DDDS = new Set([
    11, 12, 13, 14, 15, 16, 17, 18, 19,
    21, 22, 24, 27, 28,
    31, 32, 33, 34, 35, 37, 38,
    41, 42, 43, 44, 45, 46, 47, 48, 49,
    51, 53, 54, 55,
    61, 62, 63, 64, 65, 66, 67, 68, 69,
    71, 73, 74, 75, 77, 79,
    81, 82, 83, 84, 85, 86, 87, 88, 89,
    91, 92, 93, 94, 95, 96, 97, 98, 99,
]);

export const isValidDDD = (ddd) => VALID_DDDS.has(parseInt(ddd, 10));

/**
 * Converte qualquer entrada (digitada, colada ou vinda da API) para dígitos nacionais.
 * Aceita: "(53) 99123-4567", "+55 53 99123-4567", "055 53 991234567", "5553991234567", ...
 * @param {string} raw valor bruto
 * @returns {string} DDD + número, no máximo 11 dígitos
 */
export const toNationalDigits = (raw) => {
    let digits = String(raw ?? '').replace(/\D/g, '');
    // Prefixo de discagem nacional/internacional ("0", "00")
    digits = digits.replace(/^0+/, '');
    // Código do país só é removido quando sobra um número completo (DDD 55 também existe)
    if (digits.length >= 12 && digits.startsWith(COUNTRY_CODE)) {
        digits = digits.slice(COUNTRY_CODE.length);
    }
    return digits.slice(0, MAX_DIGITS);
};

/**
 * Formata dígitos nacionais de forma progressiva, conforme o usuário digita.
 * "5" -> "(5" | "5399" -> "(53) 99" | "5332221234" -> "(53) 3222-1234" | "53991234567" -> "(53) 99123-4567"
 * @param {string} digits dígitos nacionais
 * @returns {string} número formatado
 */
export const formatNational = (digits) => {
    const d = toNationalDigits(digits);
    if (!d) return '';
    if (d.length <= 2) return `(${d}`;

    const ddd = d.slice(0, 2);
    const number = d.slice(2);
    // Celular (9 dígitos) quebra em 5-4, fixo (8 dígitos) quebra em 4-4
    const split = number.length > 8 || (number[0] === '9' && number.length > 4) ? 5 : 4;
    if (number.length <= split) return `(${ddd}) ${number}`;
    return `(${ddd}) ${number.slice(0, split)}-${number.slice(split)}`;
};

/**
 * Valida um telefone brasileiro.
 * @param {string} raw valor em qualquer formato
 * @param {Object} options
 * @param {boolean} options.mobileOnly exige celular (WhatsApp)
 * @param {boolean} options.required campo obrigatório
 * @returns {{ valid: boolean, error: string|null, missing?: number }}
 *  error: 'required' | 'ddd' | 'incomplete' | 'mobile' | 'invalid' | null
 */
export const validateBrPhone = (raw, { mobileOnly = false, required = false } = {}) => {
    const d = toNationalDigits(raw);

    if (!d) return required ? { valid: false, error: 'required' } : { valid: true, error: null };
    if (d.length >= 2 && !isValidDDD(d.slice(0, 2))) return { valid: false, error: 'ddd' };

    const first = d[2];
    const isMobile = first === '9';
    const expected = isMobile || mobileOnly ? 11 : 10;

    if (d.length < expected) {
        // Número de 8 dígitos começando com 6-9 é um celular sem o 9 na frente
        if (mobileOnly && d.length === 10) return { valid: false, error: 'mobile' };
        return { valid: false, error: 'incomplete', missing: expected - d.length };
    }

    if (mobileOnly && !isMobile) return { valid: false, error: 'mobile' };
    // 10 dígitos: fixo começa com 2-5 | 11 dígitos: celular começa com 9
    if (d.length === 10 && !/[2-5]/.test(first)) return { valid: false, error: 'invalid' };
    if (d.length === 11 && !isMobile) return { valid: false, error: 'invalid' };

    return { valid: true, error: null };
};

export const isValidBrPhone = (raw, options) => validateBrPhone(raw, options).valid;

/**
 * Valor no formato esperado pela API
 * @param {string} raw valor em qualquer formato
 * @param {boolean} withCountryCode inclui o 55 (WhatsApp)
 */
export const toApiPhone = (raw, withCountryCode = false) => {
    const d = toNationalDigits(raw);
    if (!d) return '';
    return withCountryCode ? COUNTRY_CODE + d : d;
};

/**
 * Formata um número salvo para exibição
 * @param {string} raw valor em qualquer formato
 * @param {boolean} withCountryCode exibe +55 na frente
 */
export const displayBrPhone = (raw, withCountryCode = false) => {
    const formatted = formatNational(raw);
    if (!formatted) return '';
    return withCountryCode ? `+${COUNTRY_CODE} ${formatted}` : formatted;
};
